import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { EtapaDaVenda } from "@shared/mercado";

/**
 * A linha do tempo da venda: Criada → Emitida → Formalizada → Conferida → Paga.
 *
 * As etapas, as datas e o detalhe de cada uma saem de `linhaDoTempoDaVenda`
 * (shared); aqui só se pinta. Cinco colunas iguais, com o rótulo EMBAIXO da
 * bolinha: com o rótulo ao lado (o stepper antigo, de três etapas) cinco não
 * cabiam em 390px.
 *
 * ⚠️ Não é uma escada. Pagar sem conferir é normal, e a conferência pode vir
 * depois do dinheiro: cada bolinha diz se AQUELA etapa aconteceu, e o conector
 * só fica verde entre duas que aconteceram. A "próxima" (a primeira que falta)
 * ganha a borda verde — é o que o produtor está esperando.
 */

const TOM: Record<EtapaDaVenda["tom"], string> = {
  neutro: "text-muted-foreground",
  sucesso: "text-sucesso",
  atencao: "text-atencao font-semibold",
  erro: "text-erro font-semibold",
};

export function LinhaDoTempoVenda({
  etapas, onAbrirConferencia,
}: {
  etapas: EtapaDaVenda[];
  /** O detalhe "−120 kg" da etapa Conferida leva à seção da conferência. */
  onAbrirConferencia?: () => void;
}) {
  const proxima = etapas.findIndex(e => !e.feita);
  return (
    <ol className="grid grid-cols-5" data-testid="linha-do-tempo">
      {etapas.map((e, i) => {
        const conectorFeito = e.feita && !!etapas[i + 1]?.feita;
        const clicavel = e.chave === "CONFERIDA" && e.tom === "erro" && !!onAbrirConferencia;
        return (
          <li
            key={e.chave}
            className="relative flex flex-col items-center text-center min-w-0 px-0.5"
            title={e.dica ?? undefined}
            data-testid={`etapa-${e.chave}`}
          >
            {i < etapas.length - 1 && (
              <span
                aria-hidden
                className={cn("absolute top-3 left-1/2 w-full h-0.5", conectorFeito ? "bg-primary" : "bg-border")}
              />
            )}
            <span
              className={cn(
                "relative z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center text-[11px] font-bold",
                e.feita ? "bg-primary border-primary text-white"
                  : i === proxima ? "bg-card border-primary text-primary"
                    : "bg-card border-muted text-muted-foreground",
              )}
            >
              {e.feita ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={cn("mt-1 text-[11px] md:text-xs font-semibold leading-tight", e.feita || i === proxima ? "text-foreground" : "text-muted-foreground")}>
              {e.rotulo}
            </span>
            {e.quando && <span className="text-[10px] md:text-[11px] leading-tight text-muted-foreground tabular-nums">{e.quando}</span>}
            {e.detalhe && (clicavel ? (
              <button
                type="button"
                className={cn("text-[10px] md:text-[11px] leading-tight underline underline-offset-2", TOM[e.tom])}
                onClick={onAbrirConferencia}
                data-testid={`detalhe-${e.chave}`}
              >
                {e.detalhe}
              </button>
            ) : (
              <span className={cn("text-[10px] md:text-[11px] leading-tight break-words", TOM[e.tom])} data-testid={`detalhe-${e.chave}`}>
                {e.detalhe}
              </span>
            ))}
          </li>
        );
      })}
    </ol>
  );
}
